import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaUser, FaCalendarAlt, FaCheck } from 'react-icons/fa';

const CalculatorSection = styled.section`
  padding: ${props => (props.$embedded ? '0' : '100px 20px')};
  background: ${props => (props.$embedded
    ? 'transparent'
    : `linear-gradient(180deg, #282852 0%, #1e1e3f 50%, #000000 100%)`)};
  position: relative;
  overflow: hidden;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  width: 100%;
  box-sizing: border-box;
`;

const SectionTitle = styled(motion.h2)`
  text-align: center;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 800;
  margin-bottom: 12px;
  color: ${props => (props.$embedded ? '#1a1a1e' : 'white')};
`;

const SectionSubtitle = styled(motion.p)`
  text-align: center;
  font-size: 1.05rem;
  color: ${props => (props.$embedded ? 'rgba(0, 0, 0, 0.5)' : 'rgba(255, 255, 255, 0.8)')};
  margin-bottom: ${props => (props.$embedded ? '32px' : '60px')};
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  padding: 0 0.25rem;

  @media (max-width: 768px) {
    font-size: 0.95rem;
    margin-bottom: ${props => (props.$embedded ? '20px' : '40px')};
  }
`;

const CalculatorWrapper = styled(motion.div)`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  background: ${props => (props.$embedded ? '#fff' : 'rgba(255, 255, 255, 0.03)')};
  border: 1px solid ${props => (props.$embedded ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.1)')};
  border-radius: 24px;
  padding: 40px;
  backdrop-filter: ${props => (props.$embedded ? 'none' : 'blur(10px)')};
  box-shadow: ${props => (props.$embedded
    ? '0 20px 50px rgba(0, 0, 0, 0.08)'
    : '0 20px 50px rgba(0, 0, 0, 0.5)')};
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  @media (max-width: 768px) {
    padding: 18px 14px;
    border-radius: 16px;
    gap: 20px;
  }
`;

const ConfigColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  min-width: 0;

  @media (max-width: 768px) {
    gap: 22px;
  }
`;

const ResultColumn = styled.div`
  background: ${props => (props.$embedded ? '#f4f4f6' : 'rgba(0, 0, 0, 0.3)')};
  border-radius: 16px;
  padding: 30px;
  border: 1px solid ${props => (props.$embedded ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.05)')};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 20px;
  min-width: 0;

  @media (max-width: 768px) {
    padding: 18px 14px;
    border-radius: 12px;
  }
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Label = styled.label`
  font-size: 1rem;
  color: ${props => (props.$embedded ? '#1a1a1e' : 'rgba(255, 255, 255, 0.9)')};
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
`;

const Input = styled.input`
  background: ${props => (props.$embedded ? '#fff' : 'rgba(255, 255, 255, 0.05)')};
  border: 1px solid ${props => (props.$embedded ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.1)')};
  border-radius: 8px;
  padding: 12px 16px;
  color: ${props => (props.$embedded ? '#1a1a1e' : 'white')};
  font-size: 1rem;
  outline: none;
  transition: all 0.2s;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    font-size: 16px;
    padding: 11px 14px;
  }

  &:focus {
    border-color: #667eea;
    background: ${props => (props.$embedded ? '#fff' : 'rgba(255, 255, 255, 0.1)')};
  }
`;

const ModulesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const ModuleItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 15px;
  background: ${props => (props.$embedded
    ? (props.active ? 'rgba(102, 126, 234, 0.08)' : '#f8f8fa')
    : (props.active ? 'rgba(102, 126, 234, 0.1)' : 'rgba(255, 255, 255, 0.03)'))};
  border: 1px solid ${props => (props.$embedded
    ? (props.active ? '#667eea' : 'rgba(0, 0, 0, 0.08)')
    : (props.active ? '#667eea' : 'rgba(255, 255, 255, 0.1)'))};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 0;

  &:hover {
    background: ${props => (props.$embedded ? '#f0f0f4' : 'rgba(255, 255, 255, 0.08)')};
  }

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
    padding: 12px;
    gap: 10px;
  }
`;

const ModuleInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;

  h4 {
    color: ${props => (props.$embedded ? '#1a1a1e' : 'white')};
    font-size: 1.1rem;
    line-height: 1.35;
    word-break: break-word;
  }

  span {
    color: ${props => (props.$embedded ? 'rgba(0, 0, 0, 0.5)' : 'rgba(255, 255, 255, 0.6)')};
    font-size: 0.9rem;
    line-height: 1.45;
    word-break: break-word;
  }

  @media (max-width: 640px) {
    h4 {
      font-size: 0.95rem;
    }

    span {
      font-size: 0.85rem;
    }
  }
`;

const ModulePriceRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-shrink: 0;

  @media (max-width: 640px) {
    width: 100%;
  }
`;

const ModulePrice = styled.span`
  color: ${props => (props.$embedded ? '#1a1a1e' : 'white')};
  font-weight: bold;
  font-size: 0.95rem;
  white-space: nowrap;

  @media (max-width: 640px) {
    font-size: 0.9rem;
  }
`;

const Checkbox = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 6px;
  border: 2px solid ${props => props.active ? '#667eea' : 'rgba(255, 255, 255, 0.3)'};
  background: ${props => props.active ? '#667eea' : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 14px;
`;

const SummaryTitle = styled.h3`
  font-size: 1.5rem;
  color: ${props => (props.$embedded ? '#1a1a1e' : 'white')};
  margin-bottom: 24px;
  border-bottom: 1px solid ${props => (props.$embedded ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.1)')};
  padding-bottom: 16px;

  @media (max-width: 768px) {
    font-size: 1.25rem;
    margin-bottom: 18px;
    padding-bottom: 12px;
  }
`;

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 12px;
  color: ${props => (props.$embedded ? 'rgba(0, 0, 0, 0.65)' : 'rgba(255, 255, 255, 0.8)')};
  font-size: 0.95rem;

  span:first-child {
    flex: 1;
    min-width: 0;
    word-break: break-word;
    line-height: 1.4;
  }

  &.total {
    margin-top: 24px;
    padding-top: 24px;
    border-top: 1px solid ${props => (props.$embedded ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.1)')};
    font-weight: bold;
    color: ${props => (props.$embedded ? '#1a1a1e' : 'white')};
    font-size: 1.2rem;
    align-items: center;
  }
  
  &.grand-total {
    color: ${props => (props.$embedded ? '#15803d' : '#4cd964')};
    font-size: 1.8rem;
    margin-top: 10px;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 5px;

    span:first-child {
      font-size: 0.9rem;
      color: ${props => (props.$embedded ? 'rgba(0, 0, 0, 0.45)' : 'rgba(255,255,255,0.6)')};
      font-weight: normal;
      flex: unset;
    }
  }

  @media (max-width: 640px) {
    font-size: 0.9rem;

    &.total {
      font-size: 1.05rem;
      margin-top: 18px;
      padding-top: 18px;
    }

    &.grand-total {
      font-size: 1.35rem;
      align-items: flex-start;
    }
  }
`;

const Price = styled.span`
  font-variant-numeric: tabular-nums;
`;

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
        style: 'currency',
        currency: 'TRY',
        minimumFractionDigits: 2
    }).format(amount);
};

const ITEMS = [
    { id: 1, name: 'Altın Mobile Ana Paket', description: 'Raporlama, Bildirim Özelliği, 3 Kullanıcı Dahil', price: 5000 },
    { id: 2, name: 'Satıcı Hedef Yönetim Modülü', description: 'Satıcı hedeflerini yönetin', price: 1000 },
    { id: 3, name: 'Aktivite Yönetim Modülü', description: 'Saha aktivitelerini takip edin', price: 1000 },
    { id: 4, name: 'Tahsilat Modülü', description: 'Tahsilat işlemlerinizi mobil ortamda yönetin', price: 2500 },
    { id: 5, name: 'E-Fatura Süreci', description: 'E-Fatura süreçlerinizi mobil ortamda yönetin', price: 2500 },
    { id: 6, name: 'Sözleşme Süreci', description: 'Sözleşme süreçlerinizi mobil ortamda yönetin', price: 1500 },
    { id: 7, name: 'Cari Risk Limiti Süreci', description: 'Cari risk limiti süreçlerinizi mobil ortamda yönetin', price: 1500 },
];

export const PriceCalculator = ({ embedded = false }) => {
    const [duration, setDuration] = useState(1);
    const [users, setUsers] = useState(0);
    const [selectedItems, setSelectedItems] = useState([1]); // Default Main Package selected

    const toggleItem = (id) => {
        if (selectedItems.includes(id)) {
            setSelectedItems(selectedItems.filter(item => item !== id));
        } else {
            setSelectedItems([...selectedItems, id]);
        }
    };

    const extraUsers = users;
    const extraUserPrice = 850;

    const monthlyBaseTotal = selectedItems.reduce((acc, id) => {
        const item = ITEMS.find(i => i.id === id);
        return acc + (item ? item.price : 0);
    }, 0);

    const monthlyExtraUsersTotal = extraUsers * extraUserPrice;
    const monthlyTotal = monthlyBaseTotal + monthlyExtraUsersTotal;
    const grandTotal = monthlyTotal * duration;

    return (
        <CalculatorSection $embedded={embedded}>
            <Container>
                <SectionTitle
                    $embedded={embedded}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                >
                    Fiyat Hesaplama
                </SectionTitle>
                <SectionSubtitle
                    $embedded={embedded}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                >
                    İhtiyaçlarınıza uygun paketi oluşturun ve maliyetinizi hemen hesaplayın
                </SectionSubtitle>

                <CalculatorWrapper
                    $embedded={embedded}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.2 }}
                >
                    <ConfigColumn>
                        <InputGroup>
                            <Label $embedded={embedded}><FaCalendarAlt /> Süre (Ay)</Label>
                            <Input
                                $embedded={embedded}
                                type="number"
                                min="1"
                                value={duration}
                                onChange={(e) => setDuration(Math.max(1, parseInt(e.target.value) || 0))}
                            />
                        </InputGroup>

                        <InputGroup>
                            <Label $embedded={embedded}><FaUser /> İlave Kullanıcı Sayısı</Label>
                            <Input
                                $embedded={embedded}
                                type="number"
                                min="0"
                                value={users}
                                onChange={(e) => setUsers(Math.max(0, parseInt(e.target.value) || 0))}
                            />
                            <span style={{ fontSize: '0.8rem', color: embedded ? 'rgba(0,0,0,0.45)' : 'rgba(255,255,255,0.5)', marginTop: '4px' }}>
                                * Ana pakette 3 kullanıcı dahildir.
                            </span>
                        </InputGroup>

                        <ModulesList>
                            <Label $embedded={embedded}>Modüller</Label>
                            {ITEMS.map((item) => (
                                <ModuleItem
                                    key={item.id}
                                    $embedded={embedded}
                                    active={selectedItems.includes(item.id)}
                                    onClick={() => toggleItem(item.id)}
                                >
                                    <ModuleInfo $embedded={embedded}>
                                        <h4>{item.name}</h4>
                                        <span>{item.description}</span>
                                    </ModuleInfo>
                                    <ModulePriceRow>
                                        <ModulePrice $embedded={embedded}>{formatCurrency(item.price)}/ay</ModulePrice>
                                        <Checkbox active={selectedItems.includes(item.id)}>
                                            {selectedItems.includes(item.id) && <FaCheck />}
                                        </Checkbox>
                                    </ModulePriceRow>
                                </ModuleItem>
                            ))}
                        </ModulesList>
                    </ConfigColumn>

                    <ResultColumn $embedded={embedded}>
                        <div>
                            <SummaryTitle $embedded={embedded}>Hesap Özeti</SummaryTitle>
                            {selectedItems.map(id => {
                                const item = ITEMS.find(i => i.id === id);
                                return (
                                    <SummaryRow key={id} $embedded={embedded}>
                                        <span>{item.name}</span>
                                        <Price>{formatCurrency(item.price)}</Price>
                                    </SummaryRow>
                                );
                            })}

                            {extraUsers > 0 && (
                                <SummaryRow $embedded={embedded}>
                                    <span>İlave Kullanıcı Lisansı ({extraUsers} Kişi)</span>
                                    <Price>{formatCurrency(monthlyExtraUsersTotal)}</Price>
                                </SummaryRow>
                            )}
                        </div>

                        <div>
                            <SummaryRow className="total" $embedded={embedded}>
                                <span>Aylık Toplam</span>
                                <Price>{formatCurrency(monthlyTotal)}</Price>
                            </SummaryRow>
                            <SummaryRow className="grand-total" $embedded={embedded}>
                                <span>{duration} Aylık Toplam Tutar</span>
                                <Price>{formatCurrency(grandTotal)}</Price>
                            </SummaryRow>
                        </div>
                    </ResultColumn>
                </CalculatorWrapper>
            </Container>
        </CalculatorSection>
    );
};
